import { createPortal } from "react-dom";
import { twMerge } from "tailwind-merge";
import { SVGs } from "../assets";
import * as React from "react";
import Text from "./Text";
import Button, { type ButtonProps } from "./Button";
import Textarea from "./Textarea";

export type ModalRef = {
  open: () => void;
  close: () => void;
};

export type ModalClassNames = {
  overlay?: string;
  card?: string;
  header?: string;
  title?: string;
  closeButton?: string;
  body?: string;
};

export type ModalProps = {
  title?: string;
  canClose?: boolean;
  children: React.ReactNode;
  classNames?: ModalClassNames;
};

export type ModalConfirmationProps = {
  description: React.ReactNode;
  buttons: ButtonProps[];
};

export type ModalPromptProps = {
  description: React.ReactNode;
  placeholder?: string;
  buttons: ButtonProps[] | ((text: string) => ButtonProps[]);
};

const Modal = React.forwardRef<ModalRef, ModalProps>(
  ({ title, canClose = true, children, classNames }, ref) => {
    const [open, setOpen] = React.useState(false);

    const close = React.useCallback(() => {
      if (!canClose) return;
      setOpen(false);
    }, [canClose]);

    React.useImperativeHandle(
      ref,
      () => ({
        open: () => setOpen(true),
        close: () => setOpen(false),
      }),
      [],
    );

    React.useEffect(() => {
      if (!open) return;
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") close();
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, close]);

    if (!open) return null;

    const showHeader = Boolean(title) || canClose;

    return createPortal(
      <div
        className={twMerge(
          "fixed inset-0 z-[500] flex items-center justify-center bg-black/40 p-4",
          classNames?.overlay,
        )}
        onMouseDown={close}
        role="dialog"
        aria-modal="true"
        aria-label={title ?? "Modal"}
      >
        <div
          className={twMerge(
            "w-full max-w-[560px] bg-surface shadow-card rounded-[16px]",
            classNames?.card,
          )}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {showHeader && (
            <div
              className={twMerge(
                "flex items-center justify-between gap-1 px-[16px] py-[23px]",
                classNames?.header,
              )}
            >
              {title && (
                <Text
                  level={1}
                  className={twMerge("font-[500]", classNames?.title)}
                >
                  {title}
                </Text>
              )}
              {canClose && (
                <button
                  type="button"
                  aria-label="Close"
                  onClick={close}
                  className={twMerge("p-[5px]", classNames?.closeButton)}
                >
                  <img
                    className="w-[14px] h-[14px]"
                    src={SVGs.close}
                    alt="Close"
                  />
                </button>
              )}
            </div>
          )}
          <div
            className={twMerge(
              "px-[16px] pb-[22px] max-h-[calc(100vh-150px)] overflow-y-auto",
              !showHeader && "pt-[23px]",
              classNames?.body,
            )}
          >
            {children}
          </div>
        </div>
      </div>,
      document.body,
    );
  },
);

Modal.displayName = "Modal";

function Confirmation({ description, buttons }: ModalConfirmationProps) {
  return (
    <div className="space-y-[20px]">
      <Text>{description}</Text>
      <div className="flex gap-[12px] justify-end">
        {buttons.map((b, idx) => (
          <Button
            key={idx}
            {...b}
            className={twMerge(
              "w-full lg:w-auto lg:min-w-[100px]",
              b.className,
            )}
          />
        ))}
      </div>
    </div>
  );
}

function Prompt({ description, placeholder, buttons }: ModalPromptProps) {
  const [text, setText] = React.useState("");
  const resolvedButtons =
    typeof buttons === "function" ? buttons(text) : buttons;

  return (
    <div className="space-y-[20px]">
      <Text>{description}</Text>
      <Textarea
        maxLength={1000}
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="flex gap-[12px] justify-end">
        {resolvedButtons.map((b, idx) => (
          <Button
            key={idx}
            {...b}
            className={twMerge(
              "w-full lg:w-auto lg:min-w-[100px]",
              b.className,
            )}
          />
        ))}
      </div>
    </div>
  );
}

const ModalWithSubcomponents = Object.assign(Modal, { Confirmation, Prompt });

export default ModalWithSubcomponents;
